// Simulation clock — fixed 1Hz ticks decoupled from the render loop
// (AGENT.md §1). The renderer drives it with real deltas; the engine converts
// those into whole sim ticks so simulation results never depend on frame rate.

import { bus } from '../core/events';

export const TICK_INTERVAL_MS = 1000;
/** Refuse to replay more than this in one frame after a frame-delta stall. */
const MAX_CATCHUP_TICKS = 60;
/**
 * Summing 60 deltas of 1000/60 lands at 999.9999999999998, so an exact second
 * of frames would otherwise owe zero ticks. Forgive that last float hair.
 */
const EPSILON_MS = 1e-6;

// --- Background-tab catch-up -----------------------------------------------
//
// Browsers throttle rAF/setInterval in hidden tabs, so the render loop stops
// feeding the engine and wall-clock time passes unplayed. On return we learn
// the true gap from visibility timestamps instead of trusting the huge resumed
// frame delta.
//
// Policy: SIMULATE compressed with a hard cap — TECHNICAL-ARCHITECTURE.md §2.3
// prescribes exactly this shape for offline time ("replay simulation in
// accelerated coarse ticks", capped, excess "treated as managed themselves",
// one "While you were away…" summary). §2.3's 48h cap governs the cold-start
// offline replay in offlineCatchup.ts, which softens everything with grace
// floors; here the backlog replays through the LIVE tick pipeline so deaths
// take the same DeathCause path as real play (src/core/types.ts DeathCause,
// published via main.ts `publish`). Because live rules apply, the cap is
// tighter than §2.3's 48h: 8 simulated hours bounds worst-case work while
// covering any realistic tab-away; time beyond it is discarded and folded into
// a single CATCHUP_COMPLETE summary event on the bus.

/** Hard cap on background-tab catch-up: 8 simulated hours of 1Hz ticks. */
export const CATCHUP_CAP_SIM_SECONDS = 8 * 3600;
/** Hide/show flaps shorter than this are treated as noise: no catch-up. */
export const CATCHUP_FLAP_IGNORE_MS = 2000;
/**
 * Max main-thread milliseconds spent draining backlog ticks per advance()
 * slice, so returning to the tab never freezes the frame. Remaining chunks
 * continue over subsequent frames. (§2.3 budgets <500ms for its 48h coarse
 * worst case; live 1Hz ticks get the same order of budget per frame.)
 */
export const CATCHUP_SLICE_BUDGET_MS = 500;

export type TickHandler = (tickCount: number, deltaSimSeconds: number) => void;

export class TickEngine {
  private accumulatorMs = 0;
  private speed = 1;
  private paused = false;
  private tickCount = 0;
  private handlers = new Set<TickHandler>();

  private isHidden = false;
  private hiddenAtMs: number | null = null;
  /** Whole ticks queued by visibility catch-up, drained under a time budget. */
  private backlogTicks = 0;
  /**
   * The first frame delta after return still spans the hidden period, so it
   * must be dropped once — the backlog already pays for that time.
   */
  private dropNextFrameDelta = false;
  private readonly catchupSliceBudgetMs: number;

  constructor(startTickCount = 0, options: { catchupSliceBudgetMs?: number } = {}) {
    this.tickCount = startTickCount;
    this.catchupSliceBudgetMs = options.catchupSliceBudgetMs ?? CATCHUP_SLICE_BUDGET_MS;
  }

  onTick(handler: TickHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  setSpeed(multiplier: number): void {
    this.speed = Math.max(0, multiplier);
    this.paused = this.speed === 0;
    if (this.paused) this.backlogTicks = 0;
    bus.emit('SIM_SPEED_CHANGED', { multiplier: this.speed });
  }

  getSpeed(): number {
    return this.paused ? 0 : this.speed;
  }

  setPaused(paused: boolean): void {
    this.paused = paused;
    // A paused sim has no clock to catch up; drop any pending backlog.
    if (paused) this.backlogTicks = 0;
    bus.emit('SIM_SPEED_CHANGED', { multiplier: this.getSpeed() });
  }

  isPaused(): boolean {
    return this.paused;
  }

  getTickCount(): number {
    return this.tickCount;
  }

  /** Resyncs to a loaded/imported/reset save so engine and state agree. */
  setTickCount(tickCount: number): void {
    this.tickCount = tickCount;
    this.accumulatorMs = 0;
    // A wholesale state swap brings its own timeline; stale gap tracking would
    // double-replay time the new state already accounts for.
    this.backlogTicks = 0;
    this.dropNextFrameDelta = false;
    this.isHidden = false;
    this.hiddenAtMs = null;
  }

  /** Ticks still owed from visibility catch-up (drained across frames). */
  getPendingCatchupTicks(): number {
    return this.backlogTicks;
  }

  /**
   * Records the wall-clock moment the tab stopped rendering. Call from
   * visibilitychange/blur; duplicate calls are ignored.
   */
  markHidden(nowMs: number): void {
    if (this.isHidden) return;
    this.isHidden = true;
    this.hiddenAtMs = nowMs;
  }

  /**
   * Records the wall-clock moment rendering resumed and queues capped
   * catch-up ticks for the hidden gap. Emits at most one CATCHUP_COMPLETE
   * summary event per genuine hide→show transition.
   */
  markVisible(nowMs: number): void {
    if (!this.isHidden) return;
    this.isHidden = false;
    const hiddenAt = this.hiddenAtMs;
    this.hiddenAtMs = null;
    if (hiddenAt === null) return;
    // Paused means the player asked for frozen time — nothing is owed.
    if (this.paused || this.speed === 0) return;

    const hiddenMs = Math.max(0, nowMs - hiddenAt);
    // Rapid blur/focus flapping must not queue ticks or emit events.
    if (hiddenMs < CATCHUP_FLAP_IGNORE_MS) return;

    const owedTicks = Math.floor((hiddenMs * this.speed) / TICK_INTERVAL_MS);
    const simulatedTicks = Math.min(owedTicks, CATCHUP_CAP_SIM_SECONDS);
    const discardedTicks = owedTicks - simulatedTicks;

    this.backlogTicks += simulatedTicks;
    // The gap is now paid by the backlog; zero the accumulator and drop the
    // next frame delta (which spans the hidden period) so the same time can't
    // be paid twice.
    this.accumulatorMs = 0;
    this.dropNextFrameDelta = true;

    bus.emit('CATCHUP_COMPLETE', {
      hiddenMs,
      simulatedTicks,
      discardedTicks,
      capped: discardedTicks > 0,
    });
  }

  /** Feed real elapsed milliseconds; runs whatever whole ticks are owed. */
  advance(realDeltaMs: number): number {
    if (this.paused || this.speed === 0) return 0;
    this.drainBacklog();
    // The first delta after return is a lie (it spans the hidden gap); drop
    // it once. While backlog remains, later deltas are also clamped to one
    // tick as belt-and-braces. Once the backlog is empty, deltas are honest
    // again and MAX_CATCHUP_TICKS stays as backstop for non-visibility stalls.
    let deltaMs = realDeltaMs;
    if (this.dropNextFrameDelta) {
      this.dropNextFrameDelta = false;
      deltaMs = 0;
    } else if (this.backlogTicks > 0) {
      deltaMs = Math.min(deltaMs, TICK_INTERVAL_MS);
    }
    this.accumulatorMs += deltaMs * this.speed;

    let ran = 0;
    while (this.accumulatorMs >= TICK_INTERVAL_MS - EPSILON_MS && ran < MAX_CATCHUP_TICKS) {
      this.accumulatorMs -= TICK_INTERVAL_MS;
      this.runTick(TICK_INTERVAL_MS / 1000);
      ran++;
    }
    // A long stall would otherwise leave a huge backlog to grind through
    // forever; visibility catch-up (markHidden/markVisible) owns that case.
    if (this.accumulatorMs >= TICK_INTERVAL_MS - EPSILON_MS) this.accumulatorMs = 0;
    return ran;
  }

  /** Runs ticks immediately, ignoring pause — dev dashboard "Force Tick". */
  forceTicks(count: number): void {
    for (let i = 0; i < count; i++) this.runTick(TICK_INTERVAL_MS / 1000);
  }

  /**
   * Drains queued catch-up ticks within the per-slice time budget so a long
   * absence never freezes the main thread; leftovers wait for the next frame.
   * Always makes progress of at least one tick when anything is queued.
   */
  private drainBacklog(): void {
    if (this.backlogTicks <= 0) return;
    const startedAt = performance.now();
    while (this.backlogTicks > 0) {
      this.backlogTicks--;
      this.runTick(TICK_INTERVAL_MS / 1000);
      if (this.backlogTicks > 0 && performance.now() - startedAt >= this.catchupSliceBudgetMs) {
        break;
      }
    }
  }

  private runTick(deltaSimSeconds: number): void {
    this.tickCount++;
    for (const handler of [...this.handlers]) {
      try {
        handler(this.tickCount, deltaSimSeconds);
      } catch (err) {
        console.error('[TickEngine] tick handler threw', err);
      }
    }
    bus.emit('TICK', { tickCount: this.tickCount, deltaSimSeconds });
  }
}
